import type { AppRouter } from '@dentaly/server/trpc'
import { TRPCClientError } from '@trpc/client'
import type { inferRouterOutputs } from '@trpc/server'
import { AlertTriangle, ArrowLeft, Info, Play, Save, Send } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { trpc } from '@/trpc/client'
import { cn } from '@/utils/misc'

type Outputs = inferRouterOutputs<AppRouter>['smsReminders']
type SettingsData = Outputs['getSettings']
type Settings = SettingsData['settings']
type Calendar = keyof Settings
type CalendarSettings = Settings[Calendar]
type HistoryItem = Outputs['history']['items'][number]
type ReminderStatus = HistoryItem['status']
type RunResult = Outputs['runNow']

const CALENDARS: { value: Calendar; label: string }[] = [
	{ value: 'ZUB', label: 'Zubní ordinace' },
	{ value: 'DH', label: 'Dentální hygiena' },
]

const STATUS_LABEL: Record<
	ReminderStatus,
	{ label: string; className: string }
> = {
	SENDING: {
		label: 'Neznámý stav',
		className: 'border-amber-200 bg-amber-50 text-amber-800',
	},
	QUEUED: {
		label: 'Předáno Twiliu',
		className: 'border-sky-200 bg-sky-50 text-sky-800',
	},
	SENT: {
		label: 'Odesláno',
		className: 'border-sky-200 bg-sky-50 text-sky-800',
	},
	DELIVERED: {
		label: 'Doručeno',
		className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
	},
	UNDELIVERED: {
		label: 'Nedoručeno',
		className: 'border-red-200 bg-red-50 text-red-800',
	},
	FAILED: {
		label: 'Chyba',
		className: 'border-red-200 bg-red-50 text-red-800',
	},
	SKIPPED: {
		label: 'Přeskočeno',
		className: 'border-stone-200 bg-stone-50 text-stone-700',
	},
}

const ALL_STATUSES = 'ALL'

const CALENDAR_SKIP_LABEL: Record<string, string> = {
	inactive: 'vypnuto',
	invalid_template: 'neplatný text SMS',
}

const RUN_SKIP_LABEL: Record<string, string> = {
	quiet_hours: 'SMS se posílají jen mezi 8:00 a 20:00.',
	already_running: 'Odesílání už právě probíhá.',
	not_configured: 'Twilio není na serveru nastaveno.',
}

const TEMPLATE_PLACEHOLDER =
	'Dobrý den, připomínáme Váš termín %termin% (%ordinace%). Pokud nemůžete přijít, zavolejte nám prosím. Na tuto SMS neodpovídejte.'

// Portal look shared with the other settings pages (golden accent, beige
// cards). Explicit colours on every shadcn control: the app's theme defaults
// to dark (contexts/theme-context.tsx), so token classes like bg-background /
// bg-card / hover:bg-muted would render dark controls on this light page.
const cardClass =
	'space-y-5 rounded-xl border border-[#e8e4df] bg-white px-5 py-5'
const primaryButton = 'gap-2 bg-[#c49b5a] text-white hover:bg-[#b38a4a]'
const outlineButton =
	'gap-2 border-[#e8e4df] bg-white text-[#5c5142] hover:border-[#d7ad6c] hover:bg-[#faf8f5] hover:text-[#3d3428]'
const ghostButton = 'text-[#6b5c4c] hover:bg-[#f5ebe0] hover:text-[#3d3428]'
const fieldClass =
	'border-[#e8e4df] bg-white text-[#3d3428] placeholder:text-[#a09689] focus:border-[#d7ad6c] focus-visible:border-[#d7ad6c]'
const selectContentClass = 'border-[#e8e4df] bg-white text-[#3d3428]'
const selectItemClass = 'text-[#3d3428] focus:bg-[#f5ebe0] focus:text-[#3d3428]'
const fieldLabel = 'text-[#5c5142]'
const hintClass = 'text-xs text-[#6b5c4c]'

function errorMessage(err: unknown, fallback: string): string {
	return err instanceof TRPCClientError ? err.message : fallback
}

function useDebounced<T>(value: T, delayMs: number): T {
	const [debounced, setDebounced] = useState(value)
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delayMs)
		return () => clearTimeout(timer)
	}, [value, delayMs])
	return debounced
}

function formatDateTime(value: Date | string): string {
	return new Date(value).toLocaleString('cs-CZ', {
		day: 'numeric',
		month: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function Banner({
	tone,
	children,
}: {
	tone: 'warning' | 'info'
	children: React.ReactNode
}) {
	const Icon = tone === 'warning' ? AlertTriangle : Info
	return (
		<div
			className={cn(
				'flex gap-3 rounded-xl border px-4 py-3 text-sm',
				tone === 'warning'
					? 'border-red-200 bg-red-50 text-red-800'
					: 'border-[#e8e4df] bg-[#f5ebe0] text-[#5c5142]',
			)}
		>
			<Icon className="mt-0.5 h-4 w-4 shrink-0" />
			<div className="min-w-0 break-words">{children}</div>
		</div>
	)
}

function CheckboxField({
	id,
	checked,
	onChange,
	label,
	description,
}: {
	id: string
	checked: boolean
	onChange: (checked: boolean) => void
	label: string
	description?: string
}) {
	return (
		<div className="flex items-start gap-3">
			<Checkbox
				id={id}
				checked={checked}
				onCheckedChange={(value) => onChange(value === true)}
				className="mt-0.5 h-5 w-5 border-[#c49b5a] data-[state=checked]:bg-[#c49b5a] data-[state=checked]:text-white"
			/>
			<div className="grid gap-1">
				<Label htmlFor={id} className="cursor-pointer text-[#3d3428]">
					{label}
				</Label>
				{description && <p className={hintClass}>{description}</p>}
			</div>
		</div>
	)
}

/** Free typing (incl. an empty field); clamped to the allowed range on blur. */
function DaysInput({
	id,
	value,
	min,
	max,
	onChange,
}: {
	id: string
	value: number
	min: number
	max: number
	onChange: (value: number) => void
}) {
	const [text, setText] = useState(String(value))
	useEffect(() => setText(String(value)), [value])

	const commit = () => {
		const parsed = Number.parseInt(text, 10)
		const clamped = Number.isNaN(parsed)
			? value
			: Math.min(max, Math.max(min, parsed))
		setText(String(clamped))
		if (clamped !== value) onChange(clamped)
	}

	return (
		<Input
			id={id}
			type="number"
			inputMode="numeric"
			min={min}
			max={max}
			value={text}
			onChange={(e) => {
				setText(e.target.value)
				const parsed = Number.parseInt(e.target.value, 10)
				if (!Number.isNaN(parsed) && parsed >= min && parsed <= max)
					onChange(parsed)
			}}
			onBlur={commit}
			className={cn(fieldClass, 'w-24')}
		/>
	)
}

function TemplateEditor({
	calendar,
	value,
	onChange,
	data,
}: {
	calendar: Calendar
	value: CalendarSettings
	onChange: (value: CalendarSettings) => void
	data: SettingsData
}) {
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const template = useDebounced(value.template, 300)
	const preview = trpc.smsReminders.preview.useQuery(
		{ calendar, template, stripDiacritics: value.stripDiacritics },
		{ placeholderData: (previous) => previous },
	)
	const id = `template-${calendar}`

	const insertPlaceholder = (key: string) => {
		const token = `%${key}%`
		const textarea = textareaRef.current
		const start = textarea?.selectionStart ?? value.template.length
		const end = textarea?.selectionEnd ?? value.template.length
		onChange({
			...value,
			template:
				value.template.slice(0, start) + token + value.template.slice(end),
		})
		requestAnimationFrame(() => {
			textarea?.focus()
			textarea?.setSelectionRange(start + token.length, start + token.length)
		})
	}

	const tooLong = (preview.data?.segments ?? 0) > data.limits.maxSegments

	return (
		<div className="grid gap-2">
			<Label htmlFor={id} className={fieldLabel}>
				Text SMS
			</Label>
			<div className="flex flex-wrap gap-1.5">
				{data.placeholders.map((p) => (
					<Button
						key={p.key}
						type="button"
						variant="outline"
						size="sm"
						title={`${p.label} – např. ${p.example}`}
						onClick={() => insertPlaceholder(p.key)}
						className="h-7 rounded-md border-[#e8e4df] bg-[#faf8f5] px-2 font-mono text-xs text-[#5c5142] hover:border-[#d7ad6c] hover:bg-[#f5ebe0]"
					>
						%{p.key}%
					</Button>
				))}
			</div>
			<Textarea
				id={id}
				ref={textareaRef}
				rows={4}
				maxLength={data.limits.maxTemplateLength}
				value={value.template}
				onChange={(e) => onChange({ ...value, template: e.target.value })}
				placeholder={TEMPLATE_PLACEHOLDER}
				className={cn(fieldClass, 'resize-y')}
			/>
			<p className={hintClass}>
				Kliknutím na zástupný symbol ho vložíte na místo kurzoru.
			</p>

			<div className="mt-2 rounded-xl border border-[#f0ece6] bg-[#faf8f5] px-4 py-3">
				<div className="mb-1 text-xs font-medium uppercase tracking-wide text-[#a09689]">
					Náhled (ukázková rodina)
				</div>
				<p className="whitespace-pre-wrap break-words text-sm text-[#3d3428]">
					{value.template.trim() === '' ? (
						<span className="text-[#a09689]">Zatím žádný text.</span>
					) : (
						preview.data?.body
					)}
				</p>
				{value.template.trim() !== '' && preview.data && (
					<div
						className={cn(
							'mt-2 text-xs',
							tooLong ? 'font-medium text-red-700' : 'text-[#6b5c4c]',
						)}
					>
						{preview.data.units} znaků · {preview.data.segments} SMS (
						{preview.data.encoding})
						{preview.data.encoding === 'UCS-2' &&
							' – diakritika zkracuje jednu SMS na 70 znaků'}
					</div>
				)}
				{preview.data?.problems.map((problem) => (
					<div key={problem} className="mt-1 text-xs font-medium text-red-700">
						{problem}
					</div>
				))}
			</div>
		</div>
	)
}

function CalendarForm({
	calendar,
	value,
	onChange,
	data,
}: {
	calendar: Calendar
	value: CalendarSettings
	onChange: (value: CalendarSettings) => void
	data: SettingsData
}) {
	const { limits } = data
	return (
		<div className="space-y-6">
			<CheckboxField
				id={`active-${calendar}`}
				checked={value.active}
				onChange={(active) => onChange({ ...value, active })}
				label="Posílat připomínky"
				description="Vypnuté připomínky se neodesílají ani plánovačem, ani tlačítkem Odeslat nyní."
			/>

			<div className="grid gap-2">
				<Label htmlFor={`days-${calendar}`} className={fieldLabel}>
					Kolik dní před termínem
				</Label>
				<DaysInput
					id={`days-${calendar}`}
					value={value.daysBefore}
					min={limits.minDaysBefore}
					max={limits.maxDaysBefore}
					onChange={(daysBefore) => onChange({ ...value, daysBefore })}
				/>
				<p className={hintClass}>
					Kalendářní dny ({limits.minDaysBefore}–{limits.maxDaysBefore}).
					Termíny objednané později (nebo zmeškané dny) dostanou SMS při
					nejbližším odeslání, pokud do termínu zbývá alespoň 12 hodin.
				</p>
			</div>

			<CheckboxField
				id={`diacritics-${calendar}`}
				checked={value.stripDiacritics}
				onChange={(stripDiacritics) => onChange({ ...value, stripDiacritics })}
				label="Odstranit diakritiku"
				description="Bez diakritiky se do jedné SMS vejde 160 znaků místo 70 – zprávy jsou levnější."
			/>

			<TemplateEditor
				calendar={calendar}
				value={value}
				onChange={onChange}
				data={data}
			/>
		</div>
	)
}

function SettingsCard({ data }: { data: SettingsData }) {
	const utils = trpc.useUtils()
	const [calendar, setCalendar] = useState<Calendar>('ZUB')
	const [draft, setDraft] = useState<Settings>(data.settings)
	// Server data changed (save, refetch) → start from it again
	useEffect(() => setDraft(data.settings), [data.settings])

	const save = trpc.smsReminders.updateSettings.useMutation({
		onSuccess: () => {
			toast.success('Nastavení uloženo')
			utils.smsReminders.getSettings.invalidate()
		},
		onError: (err) =>
			toast.error(errorMessage(err, 'Nastavení se nepodařilo uložit')),
	})
	const dirty = JSON.stringify(draft) !== JSON.stringify(data.settings)

	return (
		<div className={cardClass}>
			<Tabs
				value={calendar}
				onValueChange={(value) => setCalendar(value as Calendar)}
			>
				<TabsList className="grid h-auto w-full grid-cols-2 bg-[#f5ebe0] p-1">
					{CALENDARS.map((option) => (
						<TabsTrigger
							key={option.value}
							value={option.value}
							className="gap-2 py-2 text-[#6b5c4c] data-[state=active]:bg-white data-[state=active]:text-[#3d3428]"
						>
							{option.label}
							<Badge
								variant="outline"
								className={cn(
									'px-1.5 py-0 text-[10px] font-medium',
									draft[option.value].active
										? 'border-emerald-200 bg-emerald-50 text-emerald-700'
										: 'border-stone-200 bg-stone-50 text-stone-500',
								)}
							>
								{draft[option.value].active ? 'zapnuto' : 'vypnuto'}
							</Badge>
						</TabsTrigger>
					))}
				</TabsList>
				{CALENDARS.map((option) => (
					<TabsContent key={option.value} value={option.value} className="mt-6">
						<CalendarForm
							calendar={option.value}
							value={draft[option.value]}
							onChange={(value) =>
								setDraft((current) => ({ ...current, [option.value]: value }))
							}
							data={data}
						/>
					</TabsContent>
				))}
			</Tabs>

			<div className="flex flex-wrap items-center gap-3 border-t border-[#f0ece6] pt-5">
				<Button
					type="button"
					onClick={() => save.mutate(draft)}
					disabled={save.isPending || !dirty}
					className={primaryButton}
				>
					<Save className="h-4 w-4" />
					{save.isPending ? 'Ukládám…' : 'Uložit nastavení'}
				</Button>
				{dirty && (
					<Button
						type="button"
						variant="ghost"
						onClick={() => setDraft(data.settings)}
						className={ghostButton}
					>
						Zahodit změny
					</Button>
				)}
				<span className={hintClass}>Uloží se nastavení obou kalendářů.</span>
			</div>
		</div>
	)
}

function CalendarSelect({
	id,
	value,
	onChange,
}: {
	id: string
	value: Calendar
	onChange: (value: Calendar) => void
}) {
	return (
		<Select value={value} onValueChange={(v) => onChange(v as Calendar)}>
			<SelectTrigger id={id} className={cn(fieldClass, 'w-48')}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent className={selectContentClass}>
				{CALENDARS.map((c) => (
					<SelectItem key={c.value} value={c.value} className={selectItemClass}>
						{c.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

function TestCard({ dryRun }: { dryRun: boolean }) {
	const [phone, setPhone] = useState('')
	const [calendar, setCalendar] = useState<Calendar>('ZUB')
	const sendTest = trpc.smsReminders.sendTest.useMutation({
		onSuccess: (result) =>
			toast.success(
				result.sent
					? 'Testovací SMS odeslána'
					: 'Režim bez odesílání – SMS nebyla odeslána',
			),
		onError: (err) =>
			toast.error(errorMessage(err, 'Testovací SMS se nepodařilo odeslat')),
	})

	return (
		<div className={cardClass}>
			<div>
				<h2 className="font-semibold text-[#3d3428]">Testovací SMS</h2>
				<p className="mt-1 text-sm text-[#6b5c4c]">
					Pošle <strong>uložený</strong> text s ukázkovými údaji jen na zadané
					číslo
					{dryRun && ' (v režimu bez odesílání se jen vykreslí)'}.
				</p>
			</div>
			<form
				className="flex flex-wrap items-end gap-3"
				onSubmit={(e) => {
					e.preventDefault()
					sendTest.reset()
					sendTest.mutate({ calendar, phone })
				}}
			>
				<div className="grid gap-2">
					<Label htmlFor="test-phone" className={fieldLabel}>
						Mobilní číslo
					</Label>
					<Input
						id="test-phone"
						type="tel"
						autoComplete="off"
						value={phone}
						onChange={(e) => setPhone(e.target.value)}
						placeholder="777 123 456"
						className={cn(fieldClass, 'w-48')}
					/>
				</div>
				<div className="grid gap-2">
					<Label htmlFor="test-calendar" className={fieldLabel}>
						Text pro
					</Label>
					<CalendarSelect
						id="test-calendar"
						value={calendar}
						onChange={setCalendar}
					/>
				</div>
				<Button
					type="submit"
					variant="outline"
					disabled={sendTest.isPending || phone.trim() === ''}
					className={outlineButton}
				>
					<Send className="h-4 w-4" />
					{sendTest.isPending ? 'Odesílám…' : 'Odeslat test'}
				</Button>
			</form>
			{sendTest.data && (
				<div className="rounded-xl border border-[#f0ece6] bg-[#faf8f5] px-4 py-3 text-sm text-[#3d3428]">
					{sendTest.data.body}
				</div>
			)}
		</div>
	)
}

function RunSummary({ result }: { result: RunResult }) {
	if (result.status === 'skipped') {
		return (
			<Banner tone="warning">
				{RUN_SKIP_LABEL[result.reason] ?? result.reason}
			</Banner>
		)
	}
	return (
		<div className="space-y-3">
			<div className="grid gap-2 sm:grid-cols-2">
				{result.calendars.map((c) => (
					<div
						key={c.calendar}
						className="rounded-xl border border-[#f0ece6] px-4 py-3 text-sm"
					>
						<div className="mb-1 font-medium text-[#3d3428]">
							{CALENDARS.find((option) => option.value === c.calendar)?.label ??
								c.calendar}
						</div>
						{c.skipped ? (
							<div className="text-[#6b5c4c]">
								{CALENDAR_SKIP_LABEL[c.skipped]}
							</div>
						) : (
							<dl className="grid grid-cols-2 gap-x-3 text-[#6b5c4c]">
								<dt>{result.dryRun ? 'K odeslání' : 'Odesláno'}</dt>
								<dd className="text-right font-medium text-[#3d3428]">
									{c.sent}
								</dd>
								<dt>Chyby</dt>
								<dd className="text-right">{c.failed}</dd>
								<dt>Neznámý stav</dt>
								<dd className="text-right">{c.unknown}</dd>
								<dt>Bez čísla</dt>
								<dd className="text-right">{c.skippedNoPhone}</dd>
								<dt>Už vyřízeno</dt>
								<dd className="text-right">{c.alreadyHandled}</dd>
							</dl>
						)}
					</div>
				))}
			</div>
			{result.previews && result.previews.length > 0 && (
				<ul className="max-h-80 space-y-2 overflow-y-auto pr-1">
					{result.previews.map((p) => (
						<li
							key={`${p.calendar}-${p.recipient}-${p.appointmentAt}`}
							className="rounded-xl border border-[#f0ece6] bg-[#faf8f5] px-4 py-2 text-sm"
						>
							<div className="text-xs text-[#6b5c4c]">
								{p.calendar} · {formatDateTime(p.appointmentAt)} ·{' '}
								{p.recipientName} · {p.recipient} · {p.segments} SMS
							</div>
							<div className="break-words text-[#3d3428]">{p.body}</div>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

function RunCard({ dryRunOnly }: { dryRunOnly: boolean }) {
	const utils = trpc.useUtils()
	const [confirming, setConfirming] = useState(false)
	const run = trpc.smsReminders.runNow.useMutation({
		onSettled: () => utils.smsReminders.history.invalidate(),
		onError: (err) => toast.error(errorMessage(err, 'Odeslání se nepodařilo')),
	})

	return (
		<div className={cardClass}>
			<div>
				<h2 className="font-semibold text-[#3d3428]">Odeslat nyní</h2>
				<p className="mt-1 text-sm text-[#6b5c4c]">
					Běžně odesílá plánovač (cron). Zkušební běh ukáže, komu by SMS odešly
					– nic neodešle ani neuloží.
				</p>
			</div>
			<div className="flex flex-wrap gap-3">
				<Button
					type="button"
					variant="outline"
					onClick={() => run.mutate({ dryRun: true })}
					disabled={run.isPending}
					className={outlineButton}
				>
					<Play className="h-4 w-4" />
					Zkušební běh
				</Button>
				{!dryRunOnly &&
					(confirming ? (
						<>
							<Button
								type="button"
								variant="destructive"
								onClick={() => {
									setConfirming(false)
									run.mutate({ dryRun: false })
								}}
								disabled={run.isPending}
								className="gap-2"
							>
								<Send className="h-4 w-4" />
								Opravdu odeslat pacientům
							</Button>
							<Button
								type="button"
								variant="ghost"
								onClick={() => setConfirming(false)}
								className={ghostButton}
							>
								Zrušit
							</Button>
						</>
					) : (
						<Button
							type="button"
							variant="outline"
							onClick={() => setConfirming(true)}
							disabled={run.isPending}
							className={outlineButton}
						>
							<Send className="h-4 w-4" />
							Odeslat připomínky nyní
						</Button>
					))}
			</div>
			{run.isPending && <LoadingSpinner />}
			{run.data && !run.isPending && <RunSummary result={run.data} />}
		</div>
	)
}

function HistoryCard() {
	const [status, setStatus] = useState<ReminderStatus | typeof ALL_STATUSES>(
		ALL_STATUSES,
	)
	const history = trpc.smsReminders.history.useInfiniteQuery(
		{ limit: 30, status: status === ALL_STATUSES ? undefined : status },
		{ getNextPageParam: (last) => last.nextCursor ?? undefined },
	)
	const items = history.data?.pages.flatMap((page) => page.items) ?? []

	return (
		<div className={cardClass}>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<h2 className="font-semibold text-[#3d3428]">Historie</h2>
				<Select
					value={status}
					onValueChange={(v) => setStatus(v as typeof status)}
				>
					<SelectTrigger
						className={cn(fieldClass, 'w-48')}
						aria-label="Filtr stavu"
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent className={selectContentClass}>
						<SelectItem value={ALL_STATUSES} className={selectItemClass}>
							Všechny stavy
						</SelectItem>
						{(Object.keys(STATUS_LABEL) as ReminderStatus[]).map((value) => (
							<SelectItem key={value} value={value} className={selectItemClass}>
								{STATUS_LABEL[value].label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{history.isLoading ? (
				<LoadingSpinner />
			) : history.isError ? (
				<Banner tone="warning">Historii se nepodařilo načíst.</Banner>
			) : items.length === 0 ? (
				<p className="text-sm text-[#6b5c4c]">Zatím žádné připomínky.</p>
			) : (
				<div className="-mx-5 overflow-x-auto px-5">
					<table className="w-full min-w-[560px] text-left text-sm">
						<thead className="border-b border-[#f0ece6] text-xs uppercase tracking-wide text-[#a09689]">
							<tr>
								<th className="py-2 pr-3 font-medium">Vytvořeno</th>
								<th className="py-2 pr-3 font-medium">Pacient</th>
								<th className="py-2 pr-3 font-medium">Termín</th>
								<th className="py-2 font-medium">Stav</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-[#f0ece6] text-[#3d3428]">
							{items.map((item) => (
								<tr
									key={item.id}
									title={item.body || undefined}
									className="align-top"
								>
									<td className="whitespace-nowrap py-2 pr-3">
										{formatDateTime(item.createdAt)}
									</td>
									<td className="py-2 pr-3">
										<div>{item.recipientName}</div>
										<div className="text-xs text-[#6b5c4c]">
											{item.recipient ?? '—'}
										</div>
									</td>
									<td className="whitespace-nowrap py-2 pr-3">
										{item.calendar} · {formatDateTime(item.appointmentAt)}
									</td>
									<td className="py-2">
										<Badge
											variant="outline"
											className={cn(
												'font-medium',
												STATUS_LABEL[item.status].className,
											)}
										>
											{STATUS_LABEL[item.status].label}
										</Badge>
										{(item.errorCode || item.errorMessage) && (
											<div className="mt-1 max-w-56 break-words text-xs text-red-700">
												{[item.errorCode, item.errorMessage]
													.filter(Boolean)
													.join(': ')}
											</div>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{history.hasNextPage && (
				<Button
					type="button"
					variant="outline"
					onClick={() => history.fetchNextPage()}
					disabled={history.isFetchingNextPage}
					className={outlineButton}
				>
					{history.isFetchingNextPage ? 'Načítám…' : 'Načíst další'}
				</Button>
			)}
		</div>
	)
}

export function SmsRemindersSettingsPage() {
	const navigate = useNavigate()
	const status = trpc.smsReminders.getStatus.useQuery()
	const enabled = status.data?.enabled === true
	const settings = trpc.smsReminders.getSettings.useQuery(undefined, {
		enabled,
	})

	if (status.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingSpinner size="lg" />
			</div>
		)
	}
	if (!status.data?.enabled) return <Navigate to="/settings" replace />
	const info = status.data

	return (
		<div className="min-h-screen bg-[#faf8f5]">
			<div className="mx-auto max-w-3xl space-y-6 px-6 py-6">
				<div className="flex items-center justify-between">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="inline-flex items-center gap-2 text-[#5c5142] transition-colors hover:text-[#3d3428]"
					>
						<ArrowLeft className="h-4 w-4" />
						<span>Zpět</span>
					</button>
					<h1 className="text-2xl font-semibold text-[#3d3428]">
						SMS připomínky
					</h1>
					<div className="w-16" />
				</div>

				{!info.twilioConfigured && (
					<Banner tone="warning">
						Twilio není na serveru nastaveno – SMS nelze odesílat. Chybí:{' '}
						{info.missing.join(', ')}
					</Banner>
				)}
				{info.twilioConfigured && info.missing.length > 0 && (
					<Banner tone="info">
						Neúplná konfigurace serveru: {info.missing.join(', ')}
					</Banner>
				)}
				{info.dryRun && (
					<Banner tone="info">
						Režim bez odesílání (SMS_REMINDERS_DRY_RUN) – žádná SMS se neodešle.
					</Banner>
				)}
				{!info.cronConfigured && !info.scheduleConfigured && (
					<Banner tone="info">
						Automatické odesílání není naplánované (chybí cron) – připomínky
						odejdou jen ručně.
					</Banner>
				)}

				{settings.data ? (
					<SettingsCard data={settings.data} />
				) : settings.isError ? (
					<Banner tone="warning">Nastavení se nepodařilo načíst.</Banner>
				) : (
					<div className="flex justify-center py-8">
						<LoadingSpinner />
					</div>
				)}
				<TestCard dryRun={info.dryRun} />
				<RunCard dryRunOnly={info.dryRun || !info.twilioConfigured} />
				<HistoryCard />
			</div>
		</div>
	)
}

export default SmsRemindersSettingsPage
